// 公開ノート一覧。api/public_notes.php から取って描くだけの小さな画面。
//
// public.ts（個別ノート）に相乗りさせない。ツリー描画、検索、タグ、Mermaid の
// 読み込み判定といった、一覧には要らないコードを全部持ち込むことになるため。
import { createTranslator, readI18n } from './lib/i18n.js';

(function () {
  'use strict';

  const listEl = document.getElementById('public-note-list');
  const statusEl = document.getElementById('public-note-list-status');
  const moreButton = document.getElementById('public-note-list-more');
  const i18n = readI18n();
  const t = createTranslator(i18n);

  if (!listEl) {
    return;
  }

  const PAGE_SIZE = 24;
  const TOKEN_PATTERN = /^[A-Za-z0-9_-]{8,32}$/;

  let offset = 0;
  let loading = false;

  function setStatus(message, showRetry = false) {
    if (!statusEl) {
      return;
    }
    statusEl.textContent = '';
    if (message) {
      statusEl.append(message);
    }
    if (showRetry) {
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'public-note-list-retry';
      retry.textContent = t('public_list_retry');
      retry.addEventListener('click', () => loadMore());
      statusEl.append(' ', retry);
    }
  }

  function formatDate(value) {
    const text = String(value || '');
    // "2026-08-07 09:00:00" の日付部分だけを出す。
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return match ? `${match[1]}/${match[2]}/${match[3]}` : '';
  }

  function createPlaceholder(name) {
    const placeholder = document.createElement('div');
    placeholder.className = 'public-note-card-cover public-note-card-cover-empty';
    placeholder.setAttribute('aria-hidden', 'true');
    // 扉画像が無いノートは、ノート名の頭文字を出す単色のカード面にする。
    placeholder.textContent = String(name || '')
      .trim()
      .slice(0, 1);
    return placeholder;
  }

  function createCard(note) {
    const token = String(note.token || '');
    if (!TOKEN_PATTERN.test(token)) {
      return null;
    }

    const card = document.createElement('a');
    card.className = 'public-note-card';
    card.href = `./public.php?t=${encodeURIComponent(token)}`;

    const cover = String(note.cover_image || '');
    if (cover) {
      const image = document.createElement('img');
      image.className = 'public-note-card-cover';
      image.src = cover;
      image.alt = '';
      image.loading = 'lazy';
      image.referrerPolicy = 'no-referrer';
      image.width = 600;
      image.height = 240;
      // 扉画像は生成物なので、消えていることがある。そのときは飾りを外すだけ。
      image.addEventListener('error', () => {
        image.replaceWith(createPlaceholder(note.name));
      });
      card.append(image);
    } else {
      card.append(createPlaceholder(note.name));
    }

    const body = document.createElement('div');
    body.className = 'public-note-card-body';

    const title = document.createElement('span');
    title.className = 'public-note-card-title';
    // ユーザー本文なので textContent で入れる。innerHTML は使わない。
    title.textContent = String(note.name || '');
    body.append(title);

    const updated = formatDate(note.updated_at);
    if (updated) {
      const meta = document.createElement('span');
      meta.className = 'public-note-card-meta';
      meta.textContent = `${t('public_list_updated_at')} ${updated}`;
      body.append(meta);
    }

    card.append(body);
    return card;
  }

  async function loadMore() {
    if (loading) {
      return;
    }
    loading = true;
    moreButton?.setAttribute('hidden', '');
    setStatus(t('public_list_loading'));

    try {
      const params = new URLSearchParams({ offset: String(offset), limit: String(PAGE_SIZE) });
      const response = await fetch(`api/public_notes.php?${params.toString()}`, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const data = await response.json();
      if (!response.ok || !data.ok) {
        throw new Error(data.error || 'request failed');
      }

      const notes = Array.isArray(data.notes) ? data.notes : [];
      for (const note of notes) {
        const card = createCard(note);
        if (card) {
          listEl.append(card);
        }
      }
      offset += notes.length;

      listEl.setAttribute('aria-busy', 'false');
      if (data.has_more && notes.length > 0) {
        moreButton?.removeAttribute('hidden');
        setStatus('');
      } else {
        setStatus(listEl.childElementCount === 0 ? t('public_list_empty') : '');
      }
    } catch (error) {
      listEl.setAttribute('aria-busy', 'false');
      setStatus(t('public_list_error'), true);
      console.error(error);
    } finally {
      loading = false;
    }
  }

  moreButton?.addEventListener('click', () => loadMore());
  loadMore();
})();
